import React, { useState, useEffect } from 'react';
import { Form, Card, Button, Table, Spinner, Modal } from 'react-bootstrap';
import '../styles/grid.css';
import { NavSidebar } from './components/NavSidebar';
import { useAuth } from '../context/AuthContext';
import Company from "../models/Company";
import Notification from "../models/Notification";
import { useHistory } from "react-router-dom";
import { HiOutlineBell } from 'react-icons/hi';
import { MdRefresh, MdClose, MdAdd, MdLink, MdLibraryBooks, MdOpenInNew, MdOndemandVideo } from 'react-icons/md';
import TrainingLibrary, { VIDEO_CATEGORY_ORDER } from "../models/TrainingLibrary";
import NotificationDocs, { MAX_DOC_BYTES } from "../models/NotificationDocs";
import { FaFileExport } from 'react-icons/fa';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';

const SEVERITY_INFO = 0;
const SEVERITY_ALERT = 1;
const AUDIENCE_ALL = 'all';
const AUDIENCE_COMPANY = 'company';
const AUDIENCE_USER = 'user';
const MAX_LINKS = 5;
const FETCH_LIMIT = 100; // backend cap; add an offset param server-side if history ever outgrows this

const isValidLinkUrl = (url) => /^https?:\/\/.+/i.test(url || '');

// links travels as a JSON-encoded string (same convention as company flags)
const parseNotificationLinks = (notification) => {
  try {
    const parsed = typeof notification.links === 'string'
      ? JSON.parse(notification.links)
      : notification.links;
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

// DB timestamps are UTC wall-clock strings with no timezone marker;
// parse as UTC then convert to the viewer's local time.
const dbMoment = (value) => moment.utc(value).local();

const severityDot = (severity) => (
  <span data-tag="allowRowEvents" style={{
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginRight: '8px',
    backgroundColor: parseInt(severity) === SEVERITY_ALERT ? '#dc3545' : '#0d6efd',
  }}/>
);

const NotificationsScreen = () => {
  const {
    user,
    isAdminOrHigher,
    isSubdomainAdminOrHigher,
  } = useAuth();
  const history = useHistory();

  // Send form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState(SEVERITY_INFO);
  const [audienceType, setAudienceType] = useState(AUDIENCE_ALL);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [expiresDate, setExpiresDate] = useState('');
  const [links, setLinks] = useState([]);
  const [isSending, setIsSending] = useState(false);

  // Ad-hoc document upload (to dst-tmp/notification-docs/)
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [isDraggingDoc, setIsDraggingDoc] = useState(false);
  const [pendingDoc, setPendingDoc] = useState(null);
  const [pendingDocLabel, setPendingDocLabel] = useState('');
  const [pendingDocDescription, setPendingDocDescription] = useState('');
  const docInputRef = React.useRef(null);

  // Link library (live listings of the S3 training doc + video buckets)
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryDocs, setLibraryDocs] = useState(null);
  const [libraryVideos, setLibraryVideos] = useState(null);
  const [libraryError, setLibraryError] = useState(null);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');

  // Data state
  const [companies, setCompanies] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);

  // History table filters
  const [searchText, setSearchText] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAudience, setFilterAudience] = useState('all');
  const [filterSender, setFilterSender] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterMinSeen, setFilterMinSeen] = useState('');
  const [filterHasLinks, setFilterHasLinks] = useState(false);

  useEffect(() => {
    if (!(isAdminOrHigher() || isSubdomainAdminOrHigher())) {
      history.push('/');
      return;
    }
    fetchCompanies();
    fetchNotifications();
  }, []);

  const fetchCompanies = async () => {
    try {
      const allCompanies = await Company.all();
      setCompanies(allCompanies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error(`Error fetching companies: ${error.message || error}`);
    }
  };

  const fetchNotifications = async () => {
    setIsLoadingNotifications(true);
    try {
      const recent = await Notification.recent(FETCH_LIMIT);
      setNotifications(recent);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error(`Error fetching notifications: ${error.message || error}`);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Load the recipient list lazily, only when the detail modal opens
  useEffect(() => {
    if (!selectedNotification) {
      setRecipients(null);
      return;
    }
    let cancelled = false;
    const fetchRecipients = async () => {
      setIsLoadingRecipients(true);
      try {
        const result = await Notification.recipients(selectedNotification.uuid);
        if (!cancelled) setRecipients(result);
      } catch (error) {
        console.error('Error fetching recipients:', error);
        if (!cancelled) setRecipients([]);
      } finally {
        if (!cancelled) setIsLoadingRecipients(false);
      }
    };
    fetchRecipients();
    return () => { cancelled = true; };
  }, [selectedNotification]);

  const companyDisplayName = (company) => company?.title || company?.name || '--';

  const isCompanySelected = (company) =>
    selectedCompanies.some(selected => selected.uuid === company.uuid);

  const filteredCompanies = () => {
    const query = companySearch.trim().toLowerCase();
    if (query === '') return [];
    return companies
      .filter(company => !isCompanySelected(company))
      .filter(company => companyDisplayName(company).toLowerCase().includes(query))
      .slice(0, 8);
  };

  const addCompany = (company) => {
    setSelectedCompanies([...selectedCompanies, company]);
    setCompanySearch('');
  };

  const removeCompany = (company) => {
    setSelectedCompanies(selectedCompanies.filter(selected => selected.uuid !== company.uuid));
  };

  const selectedNamesText = (max = 3) => {
    const names = selectedCompanies.map(companyDisplayName);
    if (names.length <= max) return names.join(', ');
    return `${names.slice(0, max).join(', ')} +${names.length - max} more`;
  };

  const audienceSummary = () => {
    if (audienceType === AUDIENCE_ALL) return 'This will be sent to ALL users of the app.';
    if (selectedCompanies.length === 0) return 'Search and add at least one company.';
    if (selectedCompanies.length === 1) return `This will be sent to all users of ${selectedNamesText()}.`;
    return `This will be sent to all users of ${selectedCompanies.length} companies: ${selectedNamesText()}.`;
  };

  // Link rows the admin actually filled in (fully empty rows are ignored)
  const filledLinks = () => links
    .map(link => ({ label: (link.label || '').trim(), url: (link.url || '').trim() }))
    .filter(link => link.label !== '' || link.url !== '');

  const linksAreValid = () => filledLinks().every(link => isValidLinkUrl(link.url));

  const addLinkRow = () => {
    if (links.length < MAX_LINKS) setLinks([...links, { label: '', url: '' }]);
  };

  const removeLinkRow = (index) => {
    setLinks(links.filter((link, i) => i !== index));
  };

  const updateLinkRow = (index, field, value) => {
    setLinks(links.map((link, i) => (i === index ? { ...link, [field]: value } : link)));
  };

  const canSend = () => {
    if (isSending) return false;
    if (title.trim() === '') return false;
    if (audienceType === AUDIENCE_COMPANY && selectedCompanies.length === 0) return false;
    if (!linksAreValid()) return false;
    return true;
  };

  const handleSend = async () => {
    if (!canSend()) return;
    const audienceText = audienceType === AUDIENCE_ALL
      ? 'EVERYONE (all users of the app)'
      : `all users of: ${selectedCompanies.map(companyDisplayName).join(', ')}`;
    if (!window.confirm(`Send "${title.trim()}" to ${audienceText}?`)) return;

    setIsSending(true);
    try {
      const input = {
        title: title.trim(),
        body: body.trim() === '' ? null : body.trim(),
        severity: severity,
        audience_type: audienceType,
        company_ids: audienceType === AUDIENCE_COMPANY
          ? selectedCompanies.map(company => parseInt(company.id))
          : null,
        created_by_user_id: user?.id ? parseInt(user.id) : null,
        expires_at: expiresDate === '' ? null :
          moment(`${expiresDate} 23:59:59`, 'YYYY-MM-DD HH:mm:ss').utc().format('YYYY-MM-DD HH:mm:ss'),
        links: filledLinks().length === 0 ? null :
          JSON.stringify(filledLinks().map(link => ({ label: link.label || link.url, url: link.url }))),
      };
      await Notification.send(input);
      toast.success('Notification sent');
      setTitle('');
      setBody('');
      setExpiresDate('');
      setSelectedCompanies([]);
      setLinks([]);
      fetchNotifications();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(`Error sending notification: ${error.message || error}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleRetract = async (notification) => {
    if (!window.confirm(`Retract "${notification.title}"? It will disappear from all users' notifications.`)) return;
    try {
      await Notification.archive(notification.uuid);
      toast.success('Notification retracted');
      fetchNotifications();
    } catch (error) {
      console.error('Error retracting notification:', error);
      toast.error(`Error retracting notification: ${error.message || error}`);
    }
  };

  const sentToText = (notification) => {
    if (notification.audience_type === AUDIENCE_ALL) return 'Everyone';
    if (notification.audience_type === AUDIENCE_USER) {
      const count = parseInt(notification.target_user_count || 0);
      return `${count} targeted ${count === 1 ? 'user' : 'users'}`;
    }
    return notification.company_names || notification.company_name || '--';
  };

  const senderText = (notification) => {
    if (notification.created_by_name) return notification.created_by_name;
    return notification.audience_type === AUDIENCE_USER ? 'System' : '--';
  };

  // Copy a sent notification back into the form; Send then creates a fresh one
  const handleResend = (notification) => {
    if (notification.audience_type === AUDIENCE_USER) {
      toast.info('System-targeted notifications are managed automatically and can\'t be resent.');
      return;
    }
    setTitle(notification.title || '');
    setBody(notification.body || '');
    setSeverity(parseInt(notification.severity) === SEVERITY_ALERT ? SEVERITY_ALERT : SEVERITY_INFO);
    if (notification.audience_type === AUDIENCE_COMPANY) {
      const targetIds = `${notification.company_ids || notification.company_id || ''}`
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');
      const matched = companies.filter(company => targetIds.includes(`${company.id}`));
      setAudienceType(AUDIENCE_COMPANY);
      setSelectedCompanies(matched);
      if (matched.length !== targetIds.length) {
        toast.warn('Some original companies could not be matched — check the selection.');
      }
    } else {
      setAudienceType(AUDIENCE_ALL);
      setSelectedCompanies([]);
    }
    setLinks(parseNotificationLinks(notification).map(link => ({
      label: link.label || '',
      url: link.url || '',
    })));
    setExpiresDate(''); // deliberately not copied — the admin sets a fresh expiry if needed
    setCompanySearch('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info('Copied to the form — review and send.');
  };

  const isExpired = (notification) =>
    notification.expires_at && dbMoment(notification.expires_at).isBefore(moment());

  const notificationStatus = (notification) => {
    if (parseInt(notification.archived) === 1) return 'Retracted';
    if (isExpired(notification)) return 'Expired';
    return 'Active';
  };

  const uniqueSenders = () =>
    [...new Set(notifications.map(n => n.created_by_name).filter(Boolean))].sort();

  const filtersActive = () =>
    searchText.trim() !== '' || filterSeverity !== 'all' || filterStatus !== 'all' ||
    filterAudience !== 'all' || filterSender !== 'all' || filterDateFrom !== '' ||
    filterDateTo !== '' || filterMinSeen !== '' || filterHasLinks;

  const clearFilters = () => {
    setSearchText('');
    setFilterSeverity('all');
    setFilterStatus('all');
    setFilterAudience('all');
    setFilterSender('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterMinSeen('');
    setFilterHasLinks(false);
  };

  const filteredNotifications = () => notifications.filter(notification => {
    const query = searchText.trim().toLowerCase();
    if (query !== '') {
      const haystack = [
        notification.title, notification.body, notification.company_names,
        notification.company_name, notification.created_by_name,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    const isAlert = parseInt(notification.severity) === SEVERITY_ALERT;
    if (filterSeverity === 'alert' && !isAlert) return false;
    if (filterSeverity === 'info' && isAlert) return false;
    if (filterStatus !== 'all' && notificationStatus(notification).toLowerCase() !== filterStatus) return false;
    if (filterAudience === 'everyone' && notification.audience_type !== AUDIENCE_ALL) return false;
    if (filterAudience === 'company' && notification.audience_type !== AUDIENCE_COMPANY) return false;
    if (filterAudience === 'user' && notification.audience_type !== AUDIENCE_USER) return false;
    if (filterSender !== 'all' && notification.created_by_name !== filterSender) return false;
    if (filterDateFrom !== '' &&
        dbMoment(notification.created_at).isBefore(moment(filterDateFrom, 'YYYY-MM-DD').startOf('day'))) return false;
    if (filterDateTo !== '' &&
        dbMoment(notification.created_at).isAfter(moment(filterDateTo, 'YYYY-MM-DD').endOf('day'))) return false;
    if (filterMinSeen !== '' && parseInt(notification.read_count || 0) < parseInt(filterMinSeen)) return false;
    if (filterHasLinks && parseNotificationLinks(notification).length === 0) return false;
    return true;
  });

  const csvEscape = (value) => {
    const cleaned = `${value == null ? '' : value}`.replace(/[\r\n]+/g, ' ').trim();
    return (cleaned.includes(',') || cleaned.includes('"'))
      ? `"${cleaned.replace(/"/g, '""')}"`
      : cleaned;
  };

  // Exports the FILTERED set — what you see is what you get
  const handleExportCSV = () => {
    const rows = filteredNotifications();
    if (rows.length === 0) return;
    const header = ['uuid', 'title', 'message', 'severity', 'audience', 'companies', 'sent_by',
      'seen', 'status', 'links', 'sent_at', 'expires_at'];
    const dataRows = rows.map(notification => [
      notification.uuid,
      notification.title,
      notification.body || '',
      parseInt(notification.severity) === SEVERITY_ALERT ? 'Alert' : 'Info',
      notification.audience_type === AUDIENCE_ALL ? 'Everyone' :
        (notification.audience_type === AUDIENCE_USER ? 'Targeted users' : 'Companies'),
      notification.audience_type === AUDIENCE_COMPANY
        ? (notification.company_names || notification.company_name || '')
        : (notification.audience_type === AUDIENCE_USER ? sentToText(notification) : ''),
      senderText(notification) === '--' ? '' : senderText(notification),
      notification.read_count,
      notificationStatus(notification),
      parseNotificationLinks(notification).map(link => link.url).join(' | '),
      dbMoment(notification.created_at).format('YYYY-MM-DD HH:mm'),
      notification.expires_at ? dbMoment(notification.expires_at).format('YYYY-MM-DD HH:mm') : '',
    ].map(csvEscape));
    const csvContent = [header.join(','), ...dataRows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `notifications-${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const statusBadge = (notification) => {
    const status = notificationStatus(notification);
    const style = status === 'Active' ? { backgroundColor: '#e9f5ee', color: '#1e7a45' }
      : status === 'Expired' ? { backgroundColor: '#eeeeee', color: '#555555' }
      : { backgroundColor: '#fdeceb', color: '#a32d2d' };
    return <span className="badge" data-tag="allowRowEvents" style={style}>{status}</span>;
  };

  // data-tag="allowRowEvents" lets clicks on custom cell content reach the
  // DataTable's onRowClicked (otherwise only cell whitespace is clickable)
  const historyColumns = [
    {
      id: 1, name: 'Severity', sortable: true, width: '105px',
      selector: row => (parseInt(row.severity) === SEVERITY_ALERT ? 1 : 0),
      cell: row => (
        <span data-tag="allowRowEvents">
          {severityDot(row.severity)}
          {parseInt(row.severity) === SEVERITY_ALERT ? 'Alert' : 'Info'}
        </span>
      ),
    },
    {
      id: 2, name: 'Title', sortable: true, grow: 2,
      selector: row => row.title || '',
      cell: row => (
        <span data-tag="allowRowEvents" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.title}>
          {row.title}
          {parseNotificationLinks(row).length > 0 && (
            <span data-tag="allowRowEvents" className="text-muted ml-1" title={parseNotificationLinks(row).map(link => link.url).join('\n')}>
              <MdLink data-tag="allowRowEvents" style={{ verticalAlign: '-2px' }}/>{parseNotificationLinks(row).length}
            </span>
          )}
        </span>
      ),
    },
    {
      id: 3, name: 'Audience', sortable: true,
      selector: row => sentToText(row),
      cell: row => (
        <span data-tag="allowRowEvents" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sentToText(row)}>
          {sentToText(row)}
        </span>
      ),
    },
    {
      id: 4, name: 'Sent By', sortable: true, width: '125px',
      selector: row => senderText(row),
    },
    {
      id: 5, name: 'Seen', sortable: true, width: '75px', right: true,
      selector: row => parseInt(row.read_count || 0),
    },
    {
      id: 6, name: 'Status', sortable: true, width: '105px',
      selector: row => notificationStatus(row),
      cell: row => statusBadge(row),
    },
    {
      id: 7, name: 'Sent', sortable: true, width: '110px',
      selector: row => row.created_at || '',
      cell: row => (
        <span data-tag="allowRowEvents" title={dbMoment(row.created_at).format('MMM D, YYYY h:mm A')}>
          {dbMoment(row.created_at).fromNow()}
        </span>
      ),
    },
    {
      id: 8, name: '', width: '175px', right: true,
      cell: row => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mr-2"
            onClick={(e) => { e.stopPropagation(); handleResend(row); }}
          >
            Resend
          </Button>
          {parseInt(row.archived) === 1 ? (
            <span className="text-muted" style={{ fontSize: '13px' }}>Retracted</span>
          ) : (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleRetract(row); }}
            >
              Retract
            </Button>
          )}
        </span>
      ),
    },
  ];

  const historyRowStyles = [
    { when: row => parseInt(row.archived) === 1, style: { opacity: 0.55 } },
  ];

  const renderCompanyPicker = () => (
    <Form.Group className="mb-3" style={{ maxWidth: '480px' }}>
      {selectedCompanies.length > 0 && (
        <div className="d-flex flex-row flex-wrap align-items-center mb-2">
          {selectedCompanies.map(company => (
            <span
              key={company.uuid}
              className="badge badge-primary p-2 mr-2 mb-1"
              style={{ fontSize: '13px' }}
            >
              {companyDisplayName(company)}
              <MdClose
                style={{ cursor: 'pointer', verticalAlign: '-2px', marginLeft: '6px' }}
                onClick={() => removeCompany(company)}
              />
            </span>
          ))}
          {selectedCompanies.length > 1 && (
            <Button variant="link" size="sm" className="p-0 mb-1" onClick={() => setSelectedCompanies([])}>
              Clear all
            </Button>
          )}
        </div>
      )}
      <Form.Control
        type="text"
        placeholder={selectedCompanies.length === 0
          ? "Search and add companies..."
          : "Add another company..."}
        value={companySearch}
        onChange={(e) => setCompanySearch(e.target.value)}
      />
      {filteredCompanies().length > 0 && (
        <div className="border rounded mt-1">
          {filteredCompanies().map(company => (
            <div
              key={company.uuid}
              className="p-2 border-bottom"
              style={{ cursor: 'pointer' }}
              onClick={() => addCompany(company)}
            >
              {companyDisplayName(company)}
            </div>
          ))}
        </div>
      )}
      {companySearch.trim() !== '' && filteredCompanies().length === 0 && (
        <div className="text-muted mt-1" style={{ fontSize: '13px' }}>
          No matching companies (or already added).
        </div>
      )}
    </Form.Group>
  );

  // Dropping/picking a file stages it with a small form (label + required
  // description) — the actual upload happens on confirm.
  const stageDocFile = (file) => {
    if (!file) return;
    if (links.length >= MAX_LINKS) {
      toast.warning(`This notification already has the maximum of ${MAX_LINKS} links.`);
      return;
    }
    if (!NotificationDocs.isAllowedFile(file)) {
      toast.error(`That file type isn't supported. Allowed: ${NotificationDocs.allowedExtensionsLabel()}`);
      return;
    }
    if (file.size > MAX_DOC_BYTES) {
      toast.error(`File is too large — the limit is ${Math.round(MAX_DOC_BYTES / 1024 / 1024)}MB.`);
      return;
    }
    setPendingDoc(file);
    setPendingDocLabel(NotificationDocs.labelFromFile(file));
    setPendingDocDescription('');
  };

  const cancelPendingDoc = () => {
    setPendingDoc(null);
    setPendingDocLabel('');
    setPendingDocDescription('');
  };

  const uploadPendingDoc = async () => {
    if (!pendingDoc || pendingDocDescription.trim() === '') return;
    setIsUploadingDoc(true);
    setUploadPercent(0);
    try {
      const link = await NotificationDocs.upload(pendingDoc, {
        label: pendingDocLabel,
        description: pendingDocDescription,
        uploadedByEmail: user?.email,
        uploadedByName: user?.full_name,
      }, setUploadPercent);
      setLinks(previous => (previous.length < MAX_LINKS ? [...previous, link] : previous));
      toast.success(`"${pendingDoc.name}" uploaded and added as a link.`);
      cancelPendingDoc();
    } catch (error) {
      console.error('Notification doc upload failed', error);
      toast.error('Upload failed. Please try again, or add the document as a manual link.');
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const onDocInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    stageDocFile(file);
  };

  const onDocDrop = (e) => {
    e.preventDefault();
    setIsDraggingDoc(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    stageDocFile(file);
  };

  const renderPendingDocForm = () => (
    <div className="mt-2 p-3" style={{ border: '1px solid #ced4da', borderRadius: '6px', backgroundColor: '#fafafa' }}>
      <div className="mb-2" style={{ fontSize: '14px', fontWeight: 500 }}>
        {pendingDoc.name} <span className="text-muted font-weight-normal">({(pendingDoc.size / 1024 / 1024).toFixed(1)}MB)</span>
      </div>
      <Form.Control
        type="text"
        placeholder="Link label users will see (e.g. Firmware notice)"
        value={pendingDocLabel}
        onChange={(e) => setPendingDocLabel(e.target.value)}
        className="mb-2"
        style={{ maxWidth: '420px' }}
      />
      <Form.Control
        as="textarea"
        rows={2}
        placeholder="Description (required) — what is this document and why is it being sent?"
        value={pendingDocDescription}
        onChange={(e) => setPendingDocDescription(e.target.value)}
        className="mb-2"
      />
      <div className="d-flex flex-row align-items-center">
        <Button
          variant="primary"
          size="sm"
          disabled={isUploadingDoc || pendingDocDescription.trim() === ''}
          onClick={uploadPendingDoc}
          className="mr-2"
        >
          {isUploadingDoc ? (
            <span><Spinner animation="border" size="sm" className="mr-1"/> Uploading... {uploadPercent}%</span>
          ) : 'Upload & Attach'}
        </Button>
        <Button variant="outline-secondary" size="sm" disabled={isUploadingDoc} onClick={cancelPendingDoc}>
          Cancel
        </Button>
        {pendingDocDescription.trim() === '' && (
          <span className="text-muted ml-2" style={{ fontSize: '12px' }}>A description is required before uploading.</span>
        )}
      </div>
    </div>
  );

  const renderDocUploadZone = () => (pendingDoc ? renderPendingDocForm() :
    <div
      className="mt-2 p-3 text-center"
      style={{
        border: `2px dashed ${isDraggingDoc ? '#007bff' : '#ced4da'}`,
        borderRadius: '6px',
        backgroundColor: isDraggingDoc ? '#f0f7ff' : '#fafafa',
        cursor: isUploadingDoc ? 'default' : 'pointer',
      }}
      onClick={() => { if (!isUploadingDoc && docInputRef.current) docInputRef.current.click(); }}
      onDragOver={(e) => { e.preventDefault(); setIsDraggingDoc(true); }}
      onDragLeave={() => setIsDraggingDoc(false)}
      onDrop={onDocDrop}
    >
      <input
        ref={docInputRef}
        type="file"
        style={{ display: 'none' }}
        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
        onChange={onDocInputChange}
      />
      <div>
        <div style={{ fontSize: '14px' }}>
          <MdAdd style={{ verticalAlign: '-2px' }}/> Drop a document here (or click) to upload and attach it as a link
        </div>
        <div className="text-muted" style={{ fontSize: '12px' }}>
          PDF, image, or Office file up to {Math.round(MAX_DOC_BYTES / 1024 / 1024)}MB.
        </div>
      </div>
    </div>
  );

  const openLibrary = () => {
    setShowLibrary(true);
    setLibrarySearch('');
    if (libraryDocs || libraryVideos || isLoadingLibrary) return; // already fetched this visit
    setIsLoadingLibrary(true);
    setLibraryError(null);
    Promise.allSettled([TrainingLibrary.fetchDocs(), TrainingLibrary.fetchVideos()])
      .then(([docsResult, videosResult]) => {
        if (docsResult.status === 'fulfilled') setLibraryDocs(docsResult.value);
        if (videosResult.status === 'fulfilled') setLibraryVideos(videosResult.value);
        if (docsResult.status === 'rejected' || videosResult.status === 'rejected') {
          console.error('Failed to load training library',
            docsResult.reason || videosResult.reason);
          setLibraryError(docsResult.status === 'rejected' && videosResult.status === 'rejected'
            ? 'Could not load the training library. You can still add links manually.'
            : `Could not load the training ${docsResult.status === 'rejected' ? 'documents' : 'videos'} — showing what loaded.`);
        }
      })
      .finally(() => setIsLoadingLibrary(false));
  };

  const linkUrlSet = () => new Set(links.map(link => (link.url || '').trim()));

  const addLibraryLink = (doc) => {
    if (links.length >= MAX_LINKS) return;
    setLinks([...links, { label: doc.title, url: doc.url }]);
  };

  const filterLibraryItems = (items) => {
    if (!items) return [];
    const query = librarySearch.trim().toLowerCase();
    if (!query) return items;
    return items.filter(item =>
      item.title.toLowerCase().includes(query)
      || (item.description || '').toLowerCase().includes(query)
      || (item.category || '').toLowerCase().includes(query)
      || item.filename.toLowerCase().includes(query));
  };

  const renderLibraryRow = (doc) => {
    const alreadyAdded = linkUrlSet().has(doc.url);
    const atMax = links.length >= MAX_LINKS;
    return (
      <div key={doc.filename} className="d-flex flex-row align-items-center py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>
            {doc.title}
            <a href={doc.url} target="_blank" rel="noopener noreferrer" title="Preview in DST Media Viewer" className="ml-2">
              <MdOpenInNew style={{ verticalAlign: '-2px' }}/>
            </a>
          </div>
          {doc.description && (
            <div className="text-muted" style={{ fontSize: '13px' }}>{doc.description}</div>
          )}
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {doc.filename}{doc.lastModified ? ` · updated ${moment(doc.lastModified).format('MMM D, YYYY')}` : ''}
          </div>
        </div>
        <Button
          variant={alreadyAdded ? 'outline-secondary' : 'outline-primary'}
          size="sm"
          className="ml-2 flex-shrink-0"
          disabled={alreadyAdded || atMax}
          onClick={() => addLibraryLink(doc)}
        >
          {alreadyAdded ? 'Added' : <span><MdAdd style={{ verticalAlign: '-2px' }}/> Add</span>}
        </Button>
      </div>
    );
  };

  const renderVideoSections = () => {
    const videos = filterLibraryItems(libraryVideos);
    if (videos.length === 0) return null;
    const otherVideos = videos.filter(video => !video.curated);
    return (
      <div className="mt-3 pt-3" style={{ borderTop: '2px solid #dee2e6' }}>
        <div className="mb-2" style={{ fontSize: '15px', fontWeight: 600 }}>
          <MdOndemandVideo style={{ verticalAlign: '-3px' }}/> Training Videos
        </div>
        {VIDEO_CATEGORY_ORDER.map(category => {
          const categoryVideos = videos.filter(video => video.category === category);
          if (categoryVideos.length === 0) return null;
          return (
            <div className="mb-3" key={category}>
              <div className="text-muted text-uppercase" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>{category}</div>
              {categoryVideos.map(renderLibraryRow)}
            </div>
          );
        })}
        {otherVideos.length > 0 && (
          <div className="mb-2">
            <div className="text-muted text-uppercase" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>Other Files in Video Folder</div>
            {otherVideos.map(renderLibraryRow)}
          </div>
        )}
      </div>
    );
  };

  const renderLibraryModal = () => {
    const docs = filterLibraryItems(libraryDocs);
    const curatedDocs = docs.filter(doc => doc.curated);
    const otherDocs = docs.filter(doc => !doc.curated);
    return (
      <Modal show={showLibrary} onHide={() => setShowLibrary(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px' }}>
            <MdLibraryBooks style={{ verticalAlign: '-3px' }}/> Training Library
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '65vh' }}>
          <div className="text-muted mb-2" style={{ fontSize: '13px' }}>
            Live listing of the training document and video folders — newly uploaded files appear
            here automatically. Links open in the DST Media Viewer.
          </div>
          <Form.Control
            type="text"
            placeholder="Search documents and videos..."
            value={librarySearch}
            onChange={(e) => setLibrarySearch(e.target.value)}
            className="mb-2"
          />
          {links.length >= MAX_LINKS && (
            <div className="text-danger mb-2" style={{ fontSize: '13px' }}>
              This notification already has the maximum of {MAX_LINKS} links.
            </div>
          )}
          {isLoadingLibrary && (
            <div className="text-center py-4"><Spinner animation="border" size="sm"/> Loading library...</div>
          )}
          {libraryError && (
            <div className="text-danger py-2" style={{ fontSize: '14px' }}>{libraryError}</div>
          )}
          {!isLoadingLibrary && (libraryDocs || libraryVideos) && (
            <div>
              {(curatedDocs.length > 0 || otherDocs.length > 0) && (
                <div className="mb-2" style={{ fontSize: '15px', fontWeight: 600 }}>
                  <MdLink style={{ verticalAlign: '-3px' }}/> Training Documents
                </div>
              )}
              {curatedDocs.length > 0 && (
                <div className="mb-3">
                  {curatedDocs.map(renderLibraryRow)}
                </div>
              )}
              {otherDocs.length > 0 && (
                <div className="mb-2">
                  <div className="text-muted text-uppercase" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>Other Files in Training Folder</div>
                  {otherDocs.map(renderLibraryRow)}
                </div>
              )}
              {renderVideoSections()}
              {docs.length === 0 && filterLibraryItems(libraryVideos).length === 0 && (
                <div className="text-muted py-3">Nothing matches your search.</div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLibrary(false)}>Done</Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderLinksEditor = () => (
    <Form.Group className="mb-3" style={{ maxWidth: '640px' }}>
      <Form.Label className="d-block">Links (optional)</Form.Label>
      {links.map((link, index) => (
        <div key={index} className="d-flex flex-row align-items-center mb-2">
          <Form.Control
            type="text"
            placeholder="Label (e.g. Firmware guide)"
            value={link.label}
            onChange={(e) => updateLinkRow(index, 'label', e.target.value)}
            style={{ maxWidth: '200px' }}
            className="mr-2"
          />
          <Form.Control
            type="text"
            placeholder="https://..."
            value={link.url}
            onChange={(e) => updateLinkRow(index, 'url', e.target.value)}
            isInvalid={(link.url || '').trim() !== '' && !isValidLinkUrl(link.url.trim())}
            className="mr-2"
          />
          <Button variant="outline-secondary" size="sm" onClick={() => removeLinkRow(index)}>
            <MdClose/>
          </Button>
        </div>
      ))}
      {!linksAreValid() && (
        <div className="text-danger mb-1" style={{ fontSize: '13px' }}>
          Every link needs a URL starting with http:// or https://
        </div>
      )}
      {links.length < MAX_LINKS && (
        <span>
          <Button variant="outline-primary" size="sm" onClick={addLinkRow} className="mr-2">
            <MdAdd/> Add Link
          </Button>
          <Button variant="outline-primary" size="sm" onClick={openLibrary}>
            <MdLibraryBooks style={{ verticalAlign: '-2px' }}/> Add from Library
          </Button>
        </span>
      )}
      {links.length >= MAX_LINKS && (
        <div className="text-muted" style={{ fontSize: '13px' }}>Maximum of {MAX_LINKS} links.</div>
      )}
      {links.length < MAX_LINKS && renderDocUploadZone()}
    </Form.Group>
  );

  const renderSendCard = () => (
    <Card className="mb-3">
      <Card.Body>
        <h5 className="mb-3">Send a Notification</h5>
        <Form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              maxLength={255}
              placeholder="e.g. New firmware available for MSi150"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Message (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Details the user will see when they open the notification"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </Form.Group>

          <div className="d-flex flex-row flex-wrap">
            <Form.Group className="mb-3 mr-4">
              <Form.Label className="d-block">Severity</Form.Label>
              <Button
                variant={severity === SEVERITY_INFO ? 'primary' : 'outline-secondary'}
                size="sm"
                className="mr-2"
                onClick={() => setSeverity(SEVERITY_INFO)}
              >
                Info (blue)
              </Button>
              <Button
                variant={severity === SEVERITY_ALERT ? 'danger' : 'outline-secondary'}
                size="sm"
                onClick={() => setSeverity(SEVERITY_ALERT)}
              >
                Alert (red)
              </Button>
            </Form.Group>

            <Form.Group className="mb-3 mr-4">
              <Form.Label className="d-block">Send To</Form.Label>
              <Button
                variant={audienceType === AUDIENCE_ALL ? 'primary' : 'outline-secondary'}
                size="sm"
                className="mr-2"
                onClick={() => { setAudienceType(AUDIENCE_ALL); setSelectedCompanies([]); setCompanySearch(''); }}
              >
                Everyone
              </Button>
              <Button
                variant={audienceType === AUDIENCE_COMPANY ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setAudienceType(AUDIENCE_COMPANY)}
              >
                Companies
              </Button>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expires (optional)</Form.Label>
              <Form.Control
                type="date"
                value={expiresDate}
                onChange={(e) => setExpiresDate(e.target.value)}
                style={{ maxWidth: '180px' }}
              />
            </Form.Group>
          </div>

          {audienceType === AUDIENCE_COMPANY && renderCompanyPicker()}

          {renderLinksEditor()}

          <div className="d-flex flex-row align-items-center justify-content-end">
            <span className="text-muted mr-3" style={{ fontSize: '13px' }}>
              {audienceSummary()}
            </span>
            <Button variant="success" type="submit" disabled={!canSend()}>
              {isSending ? <Spinner animation="border" size="sm"/> : 'Send Notification'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderDetailModal = () => {
    const notification = selectedNotification;
    if (!notification) return null;
    const isRetracted = parseInt(notification.archived) === 1;
    const modalLinks = parseNotificationLinks(notification).filter(link => isValidLinkUrl(link.url));
    return (
      <Modal show={true} onHide={() => setSelectedNotification(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px' }}>
            {severityDot(notification.severity)}
            {notification.title}
            {isRetracted && (
              <span className="badge badge-secondary ml-2">Retracted</span>
            )}
            {!isRetracted && isExpired(notification) && (
              <span className="badge badge-secondary ml-2">Expired</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {notification.body ? (
            <p style={{ whiteSpace: 'pre-wrap' }}>{notification.body}</p>
          ) : (
            <p className="text-muted">No message body.</p>
          )}
          {modalLinks.length > 0 && (
            <div className="mb-3">
              <div className="text-muted mb-1" style={{ fontSize: '13px' }}>Links</div>
              {modalLinks.map((link, index) => (
                <div key={index}>
                  <MdLink style={{ verticalAlign: '-2px', marginRight: '6px' }}/>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.label || link.url}
                  </a>
                </div>
              ))}
            </div>
          )}
          <Table size="sm" borderless className="mb-0" style={{ fontSize: '14px' }}>
            <tbody>
              <tr>
                <td className="text-muted" style={{ width: '130px' }}>Severity</td>
                <td>{parseInt(notification.severity) === SEVERITY_ALERT ? 'Alert (red)' : 'Info (blue)'}</td>
              </tr>
              <tr>
                <td className="text-muted">Sent to</td>
                <td>{sentToText(notification)}</td>
              </tr>
              <tr>
                <td className="text-muted">Sent by</td>
                <td>{senderText(notification)}</td>
              </tr>
              <tr>
                <td className="text-muted">Sent</td>
                <td>
                  {dbMoment(notification.created_at).format('MMM D, YYYY h:mm A')}
                  {' '}({dbMoment(notification.created_at).fromNow()})
                </td>
              </tr>
              <tr>
                <td className="text-muted">Expires</td>
                <td>{notification.expires_at
                  ? dbMoment(notification.expires_at).format('MMM D, YYYY h:mm A')
                  : 'Never'}</td>
              </tr>
              <tr>
                <td className="text-muted">Seen by</td>
                <td>{notification.read_count} {parseInt(notification.read_count) === 1 ? 'user' : 'users'}</td>
              </tr>
            </tbody>
          </Table>
          <div className="mt-3 pt-2 border-top">
            <div className="text-muted mb-2" style={{ fontSize: '13px' }}>
              {notification.audience_type === AUDIENCE_USER
                ? `Recipients${recipients ? ` (${recipients.length})` : ''} — targeted by the system`
                : `Read by${recipients ? ` (${recipients.length})` : ''}`}
            </div>
            {isLoadingRecipients ? (
              <div className="d-flex justify-content-center p-2"><Spinner animation="border" size="sm"/></div>
            ) : !recipients || recipients.length === 0 ? (
              <div className="text-muted" style={{ fontSize: '13px' }}>
                {notification.audience_type === AUDIENCE_USER
                  ? 'No users targeted.'
                  : 'No one has read this yet.'}
              </div>
            ) : (
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                <Table size="sm" style={{ fontSize: '13px' }}>
                  <tbody>
                    {recipients.map(recipient => (
                      <tr key={recipient.user_id}>
                        <td>
                          {recipient.full_name || recipient.email || `User ${recipient.user_id}`}
                        </td>
                        <td className="text-muted">{recipient.company_name || '--'}</td>
                        <td className="text-right">
                          {recipient.read_at ? (
                            <span
                              className="text-success"
                              title={dbMoment(recipient.read_at).format('MMM D, YYYY h:mm A')}
                            >
                              Read {dbMoment(recipient.read_at).fromNow()}
                            </span>
                          ) : (
                            <span className="text-muted">Unread</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => { setSelectedNotification(null); handleResend(notification); }}
          >
            Resend
          </Button>
          {!isRetracted && (
            <Button
              variant="outline-danger"
              onClick={() => { setSelectedNotification(null); handleRetract(notification); }}
            >
              Retract
            </Button>
          )}
          <Button variant="secondary" onClick={() => setSelectedNotification(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderHistoryFilters = () => (
    <div className="border rounded p-2 mb-3">
      <div className="d-flex flex-row flex-wrap align-items-center">
        <Form.Control
          type="text"
          size="sm"
          placeholder="Search title, message, company, sender..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="mr-2 mb-2"
          style={{ maxWidth: '280px' }}
        />
        <Form.Control as="select" size="sm" className="mr-2 mb-2" style={{ width: 'auto' }}
          value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
          <option value="all">Severity: All</option>
          <option value="info">Info (blue)</option>
          <option value="alert">Alert (red)</option>
        </Form.Control>
        <Form.Control as="select" size="sm" className="mr-2 mb-2" style={{ width: 'auto' }}
          value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">Status: All</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="retracted">Retracted</option>
        </Form.Control>
        <Form.Control as="select" size="sm" className="mr-2 mb-2" style={{ width: 'auto' }}
          value={filterAudience} onChange={(e) => setFilterAudience(e.target.value)}>
          <option value="all">Audience: All</option>
          <option value="everyone">Everyone</option>
          <option value="company">Company-targeted</option>
          <option value="user">System-targeted users</option>
        </Form.Control>
        <Form.Control as="select" size="sm" className="mr-2 mb-2" style={{ width: 'auto' }}
          value={filterSender} onChange={(e) => setFilterSender(e.target.value)}>
          <option value="all">Sent by: All</option>
          {uniqueSenders().map(sender => (
            <option key={sender} value={sender}>{sender}</option>
          ))}
        </Form.Control>
      </div>
      <div className="d-flex flex-row flex-wrap align-items-center">
        <span className="text-muted mr-2 mb-2" style={{ fontSize: '13px' }}>Sent between</span>
        <Form.Control type="date" size="sm" className="mr-2 mb-2" style={{ width: '150px' }}
          value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}/>
        <span className="text-muted mr-2 mb-2" style={{ fontSize: '13px' }}>and</span>
        <Form.Control type="date" size="sm" className="mr-3 mb-2" style={{ width: '150px' }}
          value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}/>
        <span className="text-muted mr-2 mb-2" style={{ fontSize: '13px' }}>Seen at least</span>
        <Form.Control type="number" size="sm" min="0" className="mr-3 mb-2" style={{ width: '75px' }}
          value={filterMinSeen} onChange={(e) => setFilterMinSeen(e.target.value)}/>
        <Form.Check
          type="checkbox"
          id="filter-has-links"
          label="Has links"
          className="mr-3 mb-2"
          style={{ fontSize: '13px' }}
          checked={filterHasLinks}
          onChange={(e) => setFilterHasLinks(e.target.checked)}
        />
        <span className="flex-grow-1"/>
        <span className="text-muted mr-2 mb-2" style={{ fontSize: '13px' }}>
          Showing {filteredNotifications().length} of {notifications.length}
        </span>
        {filtersActive() && (
          <Button variant="outline-secondary" size="sm" className="mb-2" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );

  const renderRecentCard = () => (
    <Card>
      <Card.Body>
        <div className="d-flex flex-row justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Sent History</h5>
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={fetchNotifications}
              disabled={isLoadingNotifications}
            >
              <MdRefresh/> Refresh
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleExportCSV}
              disabled={isLoadingNotifications || filteredNotifications().length === 0}
            >
              <FaFileExport style={{ verticalAlign: '-2px' }}/> Export CSV ({filteredNotifications().length})
            </Button>
          </div>
        </div>
        {renderHistoryFilters()}
        <DataTable
          columns={historyColumns}
          data={filteredNotifications()}
          progressPending={isLoadingNotifications}
          progressComponent={<div className="d-flex justify-content-center p-4"><Spinner animation="border"/></div>}
          noDataComponent={
            <div className="text-muted p-3">
              {notifications.length === 0
                ? 'Nothing sent yet. Notifications you send will appear here.'
                : 'No notifications match the current filters.'}
            </div>
          }
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          dense
          noHeader
          persistTableHead
          highlightOnHover
          pointerOnHover
          onRowClicked={(row) => setSelectedNotification(row)}
          conditionalRowStyles={historyRowStyles}
          defaultSortFieldId={7}
          defaultSortAsc={false}
        />
      </Card.Body>
    </Card>
  );

  return (
    <div className="d-flex flex-row justify-content-start align-items-start">
      <NavSidebar/>
      <div className="d-flex flex-column rounded-lg bg-white p-3 mt-2"
        style={{ width: '87vw', minWidth: '800px' }}
      >
        <h3 className="mb-3"><HiOutlineBell style={{ verticalAlign: '-3px' }}/> Notifications</h3>
        {renderSendCard()}
        {renderRecentCard()}
        {renderDetailModal()}
        {renderLibraryModal()}
      </div>
    </div>
  );
};

export default NotificationsScreen;
